import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  deleteGalleryImage,
  fetchBranchGallery,
  reorderGallery,
  updateGalleryCaption,
  uploadBranchGallery,
} from '../../api/salon';
import DashboardShell from '../../components/ui/DashboardShell';
import { useToast } from '../../context/ToastContext';

const MAX_IMAGES = 6;

const SortableImage = ({ image, onDelete, onCaptionChange, captionPlaceholder }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: image._id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group relative overflow-hidden rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900"
    >
      <div
        {...attributes}
        {...listeners}
        className="absolute left-2 top-2 z-10 cursor-grab rounded bg-black/50 px-2 py-0.5 text-xs text-zinc-700 dark:text-zinc-300"
      >
        ⋮⋮
      </div>
      <button
        type="button"
        onClick={() => onDelete(image._id)}
        className="absolute right-2 top-2 z-10 hidden rounded-full bg-red-600/90 px-2 py-0.5 text-xs text-white group-hover:block"
      >
        ✕
      </button>
      <img src={image.imageUrl} alt="" className="aspect-square w-full object-cover" />
      <input
        type="text"
        value={image.caption || ''}
        onChange={(e) => onCaptionChange(image._id, e.target.value)}
        onBlur={(e) => onCaptionChange(image._id, e.target.value, true)}
        placeholder={captionPlaceholder}
        className="w-full border-t border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-xs outline-none focus:border-glow-500"
      />
    </div>
  );
};

const EmptySlot = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex aspect-square w-full items-center justify-center rounded-xl border-2 border-dashed border-zinc-300 dark:border-zinc-700 text-3xl text-zinc-600 dark:text-zinc-500 hover:border-glow-500 hover:text-glow-400"
  >
    +
  </button>
);

const BranchGalleryPage = () => {
  const { t } = useTranslation();
  const { branchId } = useParams();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const captionTimers = useRef({});

  const { data, isLoading } = useQuery({
    queryKey: ['branch-gallery', branchId],
    queryFn: () => fetchBranchGallery(branchId),
  });

  const images = data?.images || [];
  const slotsLeft = MAX_IMAGES - images.length;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const uploadMut = useMutation({
    mutationFn: (files) =>
      uploadBranchGallery(branchId, files, (event) => {
        if (event.total) {
          setUploadProgress(Math.round((event.loaded * 100) / event.total));
        }
      }),
    onSuccess: () => {
      showToast(t('toast.imagesUploaded'), 'success');
      setUploadProgress(null);
      queryClient.invalidateQueries({ queryKey: ['branch-gallery', branchId] });
    },
    onError: (err) => {
      showToast(err.response?.data?.message || t('errors.uploadFailed'), 'error');
      setUploadProgress(null);
    },
  });

  const deleteMut = useMutation({
    mutationFn: deleteGalleryImage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branch-gallery', branchId] });
    },
  });

  const reorderMut = useMutation({
    mutationFn: reorderGallery,
    onSuccess: (res) => {
      queryClient.setQueryData(['branch-gallery', branchId], (old) => ({
        ...old,
        images: res.images,
      }));
    },
  });

  const captionMut = useMutation({
    mutationFn: ({ imageId, caption }) => updateGalleryCaption(imageId, caption),
  });

  const handleUpload = (fileList) => {
    const files = Array.from(fileList);
    if (!files.length) return;
    if (files.length > slotsLeft) {
      showToast(t('toast.galleryUploadLimit', { count: slotsLeft, max: MAX_IMAGES }), 'error');
      return;
    }
    uploadMut.mutate(files);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = images.findIndex((i) => i._id === active.id);
    const newIndex = images.findIndex((i) => i._id === over.id);
    const reordered = arrayMove(images, oldIndex, newIndex);
    const items = reordered.map((img, order) => ({ imageId: img._id, order }));

    queryClient.setQueryData(['branch-gallery', branchId], (old) => ({
      ...old,
      images: reordered,
    }));

    reorderMut.mutate(items);
  };

  const handleCaptionChange = (imageId, caption, saveNow) => {
    clearTimeout(captionTimers.current[imageId]);
    queryClient.setQueryData(['branch-gallery', branchId], (old) => ({
      ...old,
      images: old.images.map((img) =>
        img._id === imageId ? { ...img, caption } : img
      ),
    }));
    if (saveNow) {
      captionMut.mutate({ imageId, caption });
    } else {
      captionTimers.current[imageId] = setTimeout(() => {
        captionMut.mutate({ imageId, caption });
      }, 600);
    }
  };

  return (
    <DashboardShell
      title={t('salon.gallery.branchTitle')}
      subtitle={t('salon.gallery.branchSubtitle', { max: MAX_IMAGES })}
    >
      <Link to="/salon/branches" className="mb-6 inline-block text-sm text-glow-400 hover:underline">
        {t('salon.gallery.backToBranches')}
      </Link>

      <div className="mb-6 flex flex-wrap items-center gap-4">
        <button
          type="button"
          disabled={slotsLeft === 0 || uploadMut.isPending}
          onClick={() => fileInputRef.current?.click()}
          className="rounded-lg bg-glow-600 px-4 py-2 text-sm font-medium text-white hover:bg-glow-500 disabled:opacity-50"
        >
          {t('salon.gallery.uploadImages')}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            handleUpload(e.target.files);
            e.target.value = '';
          }}
        />
        <span className="text-sm text-zinc-600 dark:text-zinc-500">
          {t('salon.gallery.imageCount', { current: images.length, max: MAX_IMAGES })}
        </span>
      </div>

      {uploadProgress !== null && (
        <div className="mb-6">
          <div className="mb-1 flex justify-between text-xs text-zinc-600 dark:text-zinc-500">
            <span>{t('salon.gallery.uploadingToS3')}</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
            <div
              className="h-full bg-glow-600 transition-all"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {slotsLeft === 0 && (
        <p className="mb-4 text-sm text-amber-400/90">
          {t('toast.galleryMaxReached', { max: MAX_IMAGES })}
        </p>
      )}

      {isLoading && <p className="text-zinc-600 dark:text-zinc-500">{t('salon.gallery.loading')}</p>}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={images.map((i) => i._id)} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {images.map((image) => (
              <SortableImage
                key={image._id}
                image={image}
                onDelete={(id) => deleteMut.mutate(id)}
                onCaptionChange={handleCaptionChange}
                captionPlaceholder={t('salon.gallery.captionPlaceholder')}
              />
            ))}
            {Array.from({ length: Math.min(slotsLeft, 3) }).map((_, i) => (
              <EmptySlot key={`empty-${i}`} onClick={() => fileInputRef.current?.click()} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </DashboardShell>
  );
};

export default BranchGalleryPage;
